import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Badge,
  Button,
} from '@mui/material';
import {
  Person as PersonIcon,
  DragIndicator,
  Restore as RestoreIcon
} from '@mui/icons-material';
import PdfSideViewer from '../../PdfSideViewer';

export default function AuthorMappingStep({ chunks, copyrightHolders, onUpdateChunks, fileUrl, numPages }) {
  const [draggedHolder, setDraggedHolder] = useState(null);
  const [draggedFromChunk, setDraggedFromChunk] = useState(null);
  const [draggedHolderIndex, setDraggedHolderIndex] = useState(null);
  const [originalChunkOrders, setOriginalChunkOrders] = useState({});
  const [dropZoneIndex, setDropZoneIndex] = useState(null);
  const dragLeaveTimeoutRef = useRef(null);

  // Store original copyright holder order when chunks first load
  useEffect(() => {
    if (Object.keys(originalChunkOrders).length === 0 && chunks.length > 0) {
      const orders = {};
      chunks.forEach(chunk => {
        orders[chunk.id] = [...(chunk.copyright_holders || [])];
      });
      setOriginalChunkOrders(orders);
    }
  }, [chunks, originalChunkOrders]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dragLeaveTimeoutRef.current) {
        clearTimeout(dragLeaveTimeoutRef.current);
      }
    };
  }, []);

  const handleDragStart = (holder, fromChunkId = null, holderIndex = null) => {
    if (fromChunkId && holderIndex !== null) {
      // Dragging to reorder within chunk
      setDraggedFromChunk(fromChunkId);
      setDraggedHolderIndex(holderIndex);
    }
    setDraggedHolder(holder);
  };

  const resetDragState = () => {
    setDraggedHolder(null);
    setDraggedFromChunk(null);
    setDraggedHolderIndex(null);
    setDropZoneIndex(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (chunkId) => {
    if (!draggedHolder) return;

    const updatedChunks = chunks.map((chunk) => {
      if (chunk.id === chunkId) {
        const holderExists = chunk.copyright_holders.some(
          (h) => h.unique_person_id === draggedHolder.unique_person_id ||
                 (h.forename === draggedHolder.first_name && h.surname === draggedHolder.last_name)
        );
        if (!holderExists) {
          return {
            ...chunk,
            copyright_holders: [
              ...chunk.copyright_holders,
              {
                forename: draggedHolder.first_name,
                surname: draggedHolder.last_name,
                prefix: draggedHolder.prefix || null,
                unique_person_id: draggedHolder.unique_person_id || null,
                id_location: draggedHolder.id_location || null,
                orcid: draggedHolder.orcid || null,
              },
            ],
          };
        }
      }
      return chunk;
    });

    onUpdateChunks(updatedChunks);
    resetDragState();
  };

  const handleDropReorder = (e, targetChunkId, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedFromChunk || draggedHolderIndex === null) {
      // This is an assign operation from sidebar, not a reorder
      handleDrop(targetChunkId);
      return;
    }

    if (draggedFromChunk !== targetChunkId) {
      // Cross-chunk move: NOT SUPPORTED in initial implementation
      // User must remove from source chunk and add to target manually
      resetDragState();
      return;
    }

    if (draggedHolderIndex === targetIndex) {
      // No-op: dropped in same position
      resetDragState();
      return;
    }

    const updatedChunks = chunks.map((chunk) => {
      if (chunk.id === targetChunkId) {
        const holders = [...chunk.copyright_holders];
        const [movedHolder] = holders.splice(draggedHolderIndex, 1);

        // Adjust target index if we're moving down in the same list
        const adjustedIndex = targetIndex > draggedHolderIndex ? targetIndex - 1 : targetIndex;
        holders.splice(adjustedIndex, 0, movedHolder);

        return { ...chunk, copyright_holders: holders };
      }
      return chunk;
    });

    onUpdateChunks(updatedChunks);
    resetDragState();
  };

  const handleRemoveHolderFromChunk = (chunkId, holderId) => {
    const updatedChunks = chunks.map((chunk) => {
      if (chunk.id === chunkId) {
        return {
          ...chunk,
          copyright_holders: chunk.copyright_holders.filter((h) => 
            h.unique_person_id ? h.unique_person_id !== holderId : 
            `${h.forename}-${h.surname}` !== holderId
          ),
        };
      }
      return chunk;
    });
    onUpdateChunks(updatedChunks);
  };

  const handleResetToAutoOrder = (chunkId) => {
    const updatedChunks = chunks.map((chunk) => {
      if (chunk.id === chunkId) {
        return {
          ...chunk,
          copyright_holders: [...(originalChunkOrders[chunkId] || [])]
        };
      }
      return chunk;
    });
    onUpdateChunks(updatedChunks);
  };

  // Check if the current order differs from the original auto-order
  const hasOrderChanged = (chunkId) => {
    const chunk = chunks.find(c => c.id === chunkId);
    const originalOrder = originalChunkOrders[chunkId];

    if (!chunk || !originalOrder) return false;
    if (chunk.copyright_holders.length !== originalOrder.length) return true;

    // Compare each holder's unique_person_id or name combination
    return chunk.copyright_holders.some((holder, idx) => {
      const original = originalOrder[idx];
      if (!original) return true;

      const holderId = holder.unique_person_id || `${holder.forename}-${holder.surname}`;
      const originalId = original.unique_person_id || `${original.forename}-${original.surname}`;

      return holderId !== originalId;
    });
  };

  // Function to count how many chunks a copyright holder is assigned to
  const getAssignmentCount = (holder) => {
    return chunks.filter(chunk =>
      chunk.copyright_holders.some(h =>
        h.unique_person_id === holder.unique_person_id ||
        (h.forename === holder.first_name && h.surname === holder.last_name)
      )
    ).length;
  };

  const allCopyrightHolders = [
    ...(copyrightHolders.authors || []).map(a => ({ ...a, role: 'Author' })),
    ...(copyrightHolders.editors || []).map(e => ({ ...e, role: 'Editor' })),
    ...(copyrightHolders.contributors || []).map(c => ({ ...c, role: 'Contributor' })),
  ];

  return (
    <Box sx={{ display: 'flex', height: '100%', width: '100%', gap: 3, overflow: 'hidden' }}>
      <Paper elevation={2} sx={{ width: 300, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">Copyright Holders</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Drag copyright holders to chunks to assign them
          </Typography>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          <Stack spacing={1}>
            {allCopyrightHolders.map((holder, idx) => {
              const assignmentCount = getAssignmentCount(holder);

              return (
                <Paper
                  key={idx}
                  elevation={1}
                  draggable
                  onDragStart={() => handleDragStart(holder)}
                  sx={{
                    p: 2,
                    cursor: 'grab',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      borderColor: 'primary.main',
                    },
                    '&:active': {
                      cursor: 'grabbing',
                    },
                  }}
                >
                  <Stack direction="row" spacing={3} alignItems="center">
                    <Badge
                      badgeContent={assignmentCount}
                      color="primary"
                      sx={{
                        '& .MuiBadge-badge': {
                          right: -3,
                          top: 3,
                        }
                      }}
                    >
                      <PersonIcon color="action" />
                    </Badge>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" fontWeight="medium">
                        {holder.first_name} {holder.prefix && `${holder.prefix} `} {holder.last_name}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip label={holder.role} size="small" color="primary" variant="outlined" />
                      </Stack>
                      {holder.unique_person_id && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          ID: {holder.unique_person_id}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </Box>
      </Paper>

      <Paper elevation={2} sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">Chunks ({chunks.length})</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Drop copyright holders here to assign them to specific chunks
          </Typography>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          <Stack spacing={2}>
            {chunks.map((chunk) => (
              <Paper
                key={chunk.id}
                elevation={1}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(chunk.id)}
                sx={{
                  p: 2.5,
                  border: '2px dashed',
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'primary.50',
                  },
                }}
              >
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {chunk.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Pages {chunk.startPage}–{chunk.endPage}
                    </Typography>
                  </Box>

                  {chunk.copyright_holders.length > 0 ? (
                    <Stack spacing={0.5}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" fontWeight="bold" color="text.secondary">
                          Assigned Copyright Holders:
                        </Typography>
                        {hasOrderChanged(chunk.id) && (
                          <Button
                            size="small"
                            startIcon={<RestoreIcon />}
                            onClick={() => handleResetToAutoOrder(chunk.id)}
                            sx={{ fontSize: '0.7rem', py: 0.25 }}
                          >
                            Reset Order
                          </Button>
                        )}
                      </Box>

                      {/* Horizontal layout with inline drop zones */}
                      <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 0.5,
                        minHeight: '32px'
                      }}>
                        {/* Drop zone at the beginning - only visible when dragging */}
                        {chunk.copyright_holders.length > 0 && draggedFromChunk === chunk.id && (
                          <Box
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (dragLeaveTimeoutRef.current) {
                                clearTimeout(dragLeaveTimeoutRef.current);
                              }
                              setDropZoneIndex(0);
                            }}
                            onDragLeave={() => {
                              dragLeaveTimeoutRef.current = setTimeout(() => {
                                setDropZoneIndex(null);
                              }, 100);
                            }}
                            onDrop={(e) => {
                              if (dragLeaveTimeoutRef.current) {
                                clearTimeout(dragLeaveTimeoutRef.current);
                              }
                              handleDropReorder(e, chunk.id, 0);
                            }}
                            sx={{
                              width: '14px',
                              height: '28px',
                              backgroundColor: dropZoneIndex === 0 ? 'primary.main' : 'divider',
                              borderRadius: 1,
                              transition: 'background-color 0.1s ease',
                              flexShrink: 0,
                            }}
                          />
                        )}

                        {chunk.copyright_holders.map((holder, idx) => {
                          const holderId = holder.unique_person_id || `${holder.forename}-${holder.surname}`;
                          const displayName = holder.prefix
                            ? `${holder.forename} ${holder.prefix} ${holder.surname}`
                            : `${holder.forename} ${holder.surname}`;

                          return (
                            <React.Fragment key={idx}>
                              {/* Holder chip with drag handle */}
                              <Box
                                draggable
                                onDragStart={() => handleDragStart(holder, chunk.id, idx)}
                                onDragEnd={resetDragState}
                                sx={{
                                  cursor: 'grab',
                                  '&:active': { cursor: 'grabbing' },
                                  opacity: draggedFromChunk === chunk.id && draggedHolderIndex === idx ? 0.5 : 1,
                                }}
                              >
                                <Chip
                                  icon={<DragIndicator sx={{ fontSize: '1rem' }} />}
                                  label={`${idx + 1}. ${displayName}`}
                                  onDelete={() => handleRemoveHolderFromChunk(chunk.id, holderId)}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ width: 'fit-content' }}
                                  title={holder.orcid ? `ORCID: ${holder.orcid}` : undefined}
                                />
                              </Box>

                              {/* Drop zone AFTER this holder (between items) - only if not the last item */}
                              {idx < chunk.copyright_holders.length - 1 && (
                                <Box
                                  onDragOver={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (dragLeaveTimeoutRef.current) {
                                      clearTimeout(dragLeaveTimeoutRef.current);
                                    }
                                    setDropZoneIndex(idx + 1);
                                  }}
                                  onDragLeave={() => {
                                    dragLeaveTimeoutRef.current = setTimeout(() => {
                                      setDropZoneIndex(null);
                                    }, 100);
                                  }}
                                  onDrop={(e) => {
                                    if (dragLeaveTimeoutRef.current) {
                                      clearTimeout(dragLeaveTimeoutRef.current);
                                    }
                                    handleDropReorder(e, chunk.id, idx + 1);
                                  }}
                                  sx={{
                                    width: '14px',
                                    height: '28px',
                                    backgroundColor:
                                      dropZoneIndex === idx + 1 && draggedFromChunk === chunk.id
                                        ? 'primary.main'
                                        : draggedFromChunk === chunk.id
                                          ? 'divider'
                                          : 'transparent',
                                    borderRadius: 1,
                                    transition: 'background-color 0.1s ease',
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                            </React.Fragment>
                          );
                        })}
                      </Box>
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                      No copyright holders assigned. Drag and drop from the left panel.
                    </Typography>
                  )}
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>
      </Paper>

      <Box sx={{ width: '50%', flexShrink: 0, minWidth: 300 }}>
        <PdfSideViewer fileUrl={fileUrl} totalPages={numPages} />
      </Box>
    </Box>
  );
}